import { BodyPartJointAngle } from "./types/bodyPartJointAngle";
import { PieChartData } from "./types/pieChartData";

const EXTENSION = "Extension";
const GREATER_THAN_20_EXTENSION = "> 20° Extension";
const TWENTY_EXTENSION_TO_20_FLEXION = "20° Extension to 20° Flexion";
const TWENTY_TO_45_FLEXION = "20° to 45° Flexion";
const FORTY_FIVE_TO_90_FLEXION = "45° to 90° Flexion";
const LESS_THAN_30_FLEXION = "< 30° Flexion";
const THIRTY_TO_60_FLEXION = "30° to 60° Flexion";
const SIXTY_TO_90_FLEXION = "60° to 90° Flexion";
const GREATER_THAN_90_FLEXION = "> 90° Flexion";
const ZERO_TO_20_FLEXION = "0° to 20° Flexion";
const GREATER_THAN_20_FLEXION = "> 20° Flexion";
const TWENTY_TO_60_FLEXION = "20° to 60° Flexion";
const GREATER_THAN_60_FLEXION = "> 60° Flexion";
const NEUTRAL = "Neutral";
const LESS_THAN_60_FLEXION = "< 60° Flexion";
const SIXTY_TO_100_FLEXION = "60° to 100° Flexion";
const GREATER_THAN_100_FLEXION = "> 100° Flexion";
const calculatePercentage = (
  totalValues: number,
  frequencyMap: Map<string, number>,
  mapKey: string
) => {
  return totalValues > 0
    ? Math.round((frequencyMap.get(mapKey)! / totalValues) * 100)
    : 0;
};
// powers the pie chart in the data deep dive section within assessment detail page
// since the values are being rounded, the percentages could potentially add up to be slightly
// more than 100 or less than 100 due to the lost precision after rounding.
class PercentagePerBodyPartChartDataService {
  getChartDataForNeck(
    bodyPartJointAngles: BodyPartJointAngle[]
  ): PieChartData[] {
    let neckSagAngles = bodyPartJointAngles.map((jointAngle) => {
      return +jointAngle.neckSag;
    });

    let totalValues = neckSagAngles.length;

    let neckSagFrequencyMap = this.getNeckSagFrequencyMap(neckSagAngles);

    const extensionPercentage = calculatePercentage(
      totalValues,
      neckSagFrequencyMap,
      EXTENSION
    );
    const zeroToTwentyPercentage = calculatePercentage(
      totalValues,
      neckSagFrequencyMap,
      ZERO_TO_20_FLEXION
    );
    const greaterThanTwentyPercentage = calculatePercentage(
      totalValues,
      neckSagFrequencyMap,
      GREATER_THAN_20_FLEXION
    );

    const data = [
      { label: EXTENSION, value: extensionPercentage, key: "neckExtension" },
      {
        label: ZERO_TO_20_FLEXION,
        value: zeroToTwentyPercentage,
        key: "neckZeroToTwentyFlexion",
      },
      {
        label: GREATER_THAN_20_FLEXION,
        value: greaterThanTwentyPercentage,
        key: "neckGreaterThanTwentyFlexion",
      },
    ];
    return data;
  }

  getChartDataForTrunk(
    bodyPartJointAngles: BodyPartJointAngle[]
  ): PieChartData[] {
    let trunkSagAngles = bodyPartJointAngles.map((jointAngle) => {
      return +jointAngle.trunkSag;
    });
    const totalValues = trunkSagAngles.length;
    let trunkSagFrequencyMap = this.getTrunkSagFrequencyMap(trunkSagAngles);
    const extensionPercentage = calculatePercentage(
      totalValues,
      trunkSagFrequencyMap,
      EXTENSION
    );
    const neutralPercentage = calculatePercentage(
      totalValues,
      trunkSagFrequencyMap,
      NEUTRAL
    );
    const zeroToTwentyPercentage = calculatePercentage(
      totalValues,
      trunkSagFrequencyMap,
      ZERO_TO_20_FLEXION
    );
    const twentyToSixtyPercentage = calculatePercentage(
      totalValues,
      trunkSagFrequencyMap,
      TWENTY_TO_60_FLEXION
    );
    const greaterThanSixtyPercentage = calculatePercentage(
      totalValues,
      trunkSagFrequencyMap,
      GREATER_THAN_60_FLEXION
    );

    const data = [
      { label: EXTENSION, value: extensionPercentage, key: "trunkExtension" },
      { label: NEUTRAL, value: neutralPercentage, key: "trunkNeutral" },
      {
        label: ZERO_TO_20_FLEXION,
        value: zeroToTwentyPercentage,
        key: "trunkZeroToTwentyFlexion",
      },
      {
        label: TWENTY_TO_60_FLEXION,
        value: twentyToSixtyPercentage,
        key: "trunkTwentyToSixtyFlexion",
      },
      {
        label: GREATER_THAN_60_FLEXION,
        value: greaterThanSixtyPercentage,
        key: "trunkGreaterThanSixtyFlexion",
      },
    ];
    return data;
  }

  getChartDataForUpperArm(
    bodyPartJointAngles: BodyPartJointAngle[]
  ): PieChartData[] {
    // for upper arm, need to decide whether to use lShoulderSag or rShoulderSag
    // based on which array has the max absolute value
    const leftShoulderSagValues = bodyPartJointAngles.map((jointAngle) => {
      return Math.abs(+jointAngle.lShoulderSag);
    });

    const rightShoulderSagValues = bodyPartJointAngles.map((jointAngle) => {
      return Math.abs(+jointAngle.rShoulderSag);
    });

    const maxLeftShoulderSag = Math.max(...leftShoulderSagValues);
    const maxRightShoulderSag = Math.max(...rightShoulderSagValues);
    const shoulderSagPropertyToUse =
      maxLeftShoulderSag > maxRightShoulderSag
        ? "lShoulderSag"
        : "rShoulderSag";

    let upperArmSagAngles = bodyPartJointAngles.map((jointAngle) => {
      return +jointAngle[
        `${shoulderSagPropertyToUse}` as keyof BodyPartJointAngle
      ];
    });

    const totalValues = upperArmSagAngles.length;
    let upperArmFrequencyMap = this.getUpperArmFrequencyMap(upperArmSagAngles);

    const lessThanNegativeTwentyAnglePercentage = calculatePercentage(
      totalValues,
      upperArmFrequencyMap,
      GREATER_THAN_20_EXTENSION
    );
    const negativeTwentyToPositiveTwentyAnglePercentage = calculatePercentage(
      totalValues,
      upperArmFrequencyMap,
      TWENTY_EXTENSION_TO_20_FLEXION
    );
    const twentyToFortyFiveAnglePercentage = calculatePercentage(
      totalValues,
      upperArmFrequencyMap,
      TWENTY_TO_45_FLEXION
    );
    const fortyFiveToNinetyAnglePercentage = calculatePercentage(
      totalValues,
      upperArmFrequencyMap,
      FORTY_FIVE_TO_90_FLEXION
    );
    const greaterThanNinetyAnglePercentage = calculatePercentage(
      totalValues,
      upperArmFrequencyMap,
      GREATER_THAN_90_FLEXION
    );

    const data = [
      {
        label: GREATER_THAN_20_EXTENSION,
        value: lessThanNegativeTwentyAnglePercentage,
        key: "upperArmGreaterThanTwentyExtension",
      },
      {
        label: TWENTY_EXTENSION_TO_20_FLEXION,
        value: negativeTwentyToPositiveTwentyAnglePercentage,
        key: "upperArmTwentyExtensionToTwentyFlexion",
      },
      {
        label: TWENTY_TO_45_FLEXION,
        value: twentyToFortyFiveAnglePercentage,
        key: "upperArmTwentyToFortyFiveFlexion",
      },
      {
        label: FORTY_FIVE_TO_90_FLEXION,
        value: fortyFiveToNinetyAnglePercentage,
        key: "upperArmFortyFiveToNinetyFlexion",
      },
      {
        label: GREATER_THAN_90_FLEXION,
        value: greaterThanNinetyAnglePercentage,
        key: "upperArmGreaterThanNinetyFlexion",
      },
    ];
    return data;
  }

  getChartDataForLowerArm(
    bodyPartJointAngles: BodyPartJointAngle[]
  ): PieChartData[] {
    // for lower arm need to decide whether to use lElbow or rElbow
    // depending on which array has the largest absolute value
    const leftElbowValues = bodyPartJointAngles.map((jointAngle) => {
      return Math.abs(+jointAngle.lElbow);
    });

    const rightElbowValues = bodyPartJointAngles.map((jointAngle) => {
      return Math.abs(+jointAngle.rElbow);
    });

    const maxLeftElbow = Math.max(...leftElbowValues);
    const maxRightElbow = Math.max(...rightElbowValues);
    const elbowPropertyToUse =
      maxLeftElbow > maxRightElbow ? "lElbow" : "rElbow";

    let lowerArmAngles = bodyPartJointAngles.map((jointAngle) => {
      return +jointAngle[`${elbowPropertyToUse}` as keyof BodyPartJointAngle];
    });

    const totalValues = lowerArmAngles.length;
    let lowerArmFrequencyMap = this.getLowerArmFrequencyMap(lowerArmAngles);

    const lessThanSixtyAnglePercentage = calculatePercentage(
      totalValues,
      lowerArmFrequencyMap,
      LESS_THAN_60_FLEXION
    );
    const sixtyToOneHundredAnglePercentage = calculatePercentage(
      totalValues,
      lowerArmFrequencyMap,
      SIXTY_TO_100_FLEXION
    );
    const greaterThanOneHundredAnglePercentage = calculatePercentage(
      totalValues,
      lowerArmFrequencyMap,
      GREATER_THAN_100_FLEXION
    );

    const data = [
      {
        label: LESS_THAN_60_FLEXION,
        value: lessThanSixtyAnglePercentage,
        key: "lowerArmLessThanSixtyFlexion",
      },
      {
        label: SIXTY_TO_100_FLEXION,
        value: sixtyToOneHundredAnglePercentage,
        key: "lowerArmSixtyToOneHundredFlexion",
      },
      {
        label: GREATER_THAN_100_FLEXION,
        value: greaterThanOneHundredAnglePercentage,
        key: "lowerArmGreaterThanOneHundredFlexion",
      },
    ];
    return data;
  }
  getChartDataForLeg(
    bodyPartJointAngles: BodyPartJointAngle[]
  ): PieChartData[] {
    // for Knee need to decide whether to use lKneee or rKnee
    // depending on which array has the largest absolute value
    const leftKneeValues = bodyPartJointAngles.map((jointAngle) => {
      return Math.abs(+jointAngle.lKneee);
    });

    const rightKneeValues = bodyPartJointAngles.map((jointAngle) => {
      return Math.abs(+jointAngle.rElbow);
    });

    const maxLeftKnee = Math.max(...leftKneeValues);
    const maxRightKnee = Math.max(...rightKneeValues);
    const kneePropertyToUse = maxLeftKnee > maxRightKnee ? "lKneee" : "rKnee";

    let kneeAngles = bodyPartJointAngles.map((jointAngle) => {
      return +jointAngle[`${kneePropertyToUse}` as keyof BodyPartJointAngle];
    });

    const totalValues = kneeAngles.length;
    let legFrequencyMap = this.getLegFrequencyMap(kneeAngles);

    const lessThanThirtyAnglePercentage = calculatePercentage(
      totalValues,
      legFrequencyMap,
      LESS_THAN_30_FLEXION
    );
    const thirtyToSixtyAnglePercentage = calculatePercentage(
      totalValues,
      legFrequencyMap,
      THIRTY_TO_60_FLEXION
    );
    const sixtyToNinetyAnglePercentage = calculatePercentage(
      totalValues,
      legFrequencyMap,
      SIXTY_TO_90_FLEXION
    );
    const greaterThanNinetyAnglePercentage = calculatePercentage(
      totalValues,
      legFrequencyMap,
      GREATER_THAN_90_FLEXION
    );

    const data = [
      {
        label: LESS_THAN_30_FLEXION,
        value: lessThanThirtyAnglePercentage,
        key: "kneeLessThanThirtyFlexion",
      },
      {
        label: THIRTY_TO_60_FLEXION,
        value: thirtyToSixtyAnglePercentage,
        key: "kneeThirtyToSixtyFlexion",
      },
      {
        label: SIXTY_TO_90_FLEXION,
        value: sixtyToNinetyAnglePercentage,
        key: "kneeSixtyToNinetyFlexion",
      },
      {
        label: GREATER_THAN_90_FLEXION,
        value: greaterThanNinetyAnglePercentage,
        key: "kneeGreaterThanNinetyFlexion",
      },
    ];
    return data;
  }

  private getNeckSagFrequencyMap(neckSagAngles: number[]): Map<string, number> {
    const NECK_EXTENSION_THRESHOLD = 0;
    let neckSagFrequencyMap = new Map<string, number>([
      [EXTENSION, 0],
      [ZERO_TO_20_FLEXION, 0],
      [GREATER_THAN_20_FLEXION, 0],
    ]);

    return neckSagAngles.reduce(
      (neckSagFrequencyMap: Map<string, number>, neckSagAngle: number) => {
        if (neckSagAngle < NECK_EXTENSION_THRESHOLD) {
          neckSagFrequencyMap.set(
            EXTENSION,
            neckSagFrequencyMap.get(EXTENSION)! + 1
          );
        } else if (
          neckSagAngle >= NECK_EXTENSION_THRESHOLD &&
          neckSagAngle <= 20
        ) {
          neckSagFrequencyMap.set(
            ZERO_TO_20_FLEXION,
            neckSagFrequencyMap.get(ZERO_TO_20_FLEXION)! + 1
          );
        } else {
          neckSagFrequencyMap.set(
            GREATER_THAN_20_FLEXION,
            neckSagFrequencyMap.get(GREATER_THAN_20_FLEXION)! + 1
          );
        }
        return neckSagFrequencyMap;
      },
      neckSagFrequencyMap
    );
  }

  private getTrunkSagFrequencyMap(
    trunkSagAngles: number[]
  ): Map<string, number> {
    const TRUNK_EXTENSION_THRESHOLD = -4;
    const TRUNK_NEUTRAL_UPPER_BOUND = 4;
    // Usually, negative angles represent extensions and positive angles represent flexions
    // but for trunk, extensions begin only after -4 degrees.
    // Likewise another anomaly for trunk is that the zero to twenty flexion range is NOT actually
    // between 0 and 20. It is instead between 4 and 20.

    let trunkSagFrequencyMap = new Map<string, number>([
      [EXTENSION, 0], // x < -4
      [NEUTRAL, 0], // -4 <= x <= +4
      [ZERO_TO_20_FLEXION, 0], // 4 < x <= 20
      [TWENTY_TO_60_FLEXION, 0], // 20 < x <= 60
      [GREATER_THAN_60_FLEXION, 0], // x > 60
    ]);

    return trunkSagAngles.reduce(
      (trunkSagFrequencyMap: Map<string, number>, trunkSagAngle: number) => {
        if (trunkSagAngle < TRUNK_EXTENSION_THRESHOLD) {
          trunkSagFrequencyMap.set(
            EXTENSION,
            trunkSagFrequencyMap.get(EXTENSION)! + 1
          );
        } else if (
          trunkSagAngle >= TRUNK_EXTENSION_THRESHOLD &&
          trunkSagAngle <= TRUNK_NEUTRAL_UPPER_BOUND
        ) {
          trunkSagFrequencyMap.set(
            NEUTRAL,
            trunkSagFrequencyMap.get(NEUTRAL)! + 1
          );
        } else if (
          trunkSagAngle > TRUNK_NEUTRAL_UPPER_BOUND &&
          trunkSagAngle <= 20
        ) {
          trunkSagFrequencyMap.set(
            ZERO_TO_20_FLEXION,
            trunkSagFrequencyMap.get(ZERO_TO_20_FLEXION)! + 1
          );
        } else if (trunkSagAngle > 20 && trunkSagAngle <= 60) {
          trunkSagFrequencyMap.set(
            TWENTY_TO_60_FLEXION,
            trunkSagFrequencyMap.get(TWENTY_TO_60_FLEXION)! + 1
          );
        } else {
          trunkSagFrequencyMap.set(
            GREATER_THAN_60_FLEXION,
            trunkSagFrequencyMap.get(GREATER_THAN_60_FLEXION)! + 1
          );
        }
        return trunkSagFrequencyMap;
      },
      trunkSagFrequencyMap
    );
  }

  private getUpperArmFrequencyMap(
    upperArmSagAngles: number[]
  ): Map<string, number> {
    let upperArmFrequencyMap = new Map<string, number>([
      [GREATER_THAN_20_EXTENSION, 0],
      [TWENTY_EXTENSION_TO_20_FLEXION, 0],
      [TWENTY_TO_45_FLEXION, 0],
      [FORTY_FIVE_TO_90_FLEXION, 0],
      [GREATER_THAN_90_FLEXION, 0],
    ]);

    return upperArmSagAngles.reduce(
      (upperArmFrequencyMap: Map<string, number>, upperArmSagAngle: number) => {
        if (upperArmSagAngle < -20) {
          upperArmFrequencyMap.set(
            GREATER_THAN_20_EXTENSION,
            upperArmFrequencyMap.get(GREATER_THAN_20_EXTENSION)! + 1
          );
        } else if (upperArmSagAngle >= -20 && upperArmSagAngle <= 20) {
          upperArmFrequencyMap.set(
            TWENTY_EXTENSION_TO_20_FLEXION,
            upperArmFrequencyMap.get(TWENTY_EXTENSION_TO_20_FLEXION)! + 1
          );
        } else if (upperArmSagAngle > 20 && upperArmSagAngle <= 45) {
          upperArmFrequencyMap.set(
            TWENTY_TO_45_FLEXION,
            upperArmFrequencyMap.get(TWENTY_TO_45_FLEXION)! + 1
          );
        } else if (upperArmSagAngle > 45 && upperArmSagAngle <= 90) {
          upperArmFrequencyMap.set(
            FORTY_FIVE_TO_90_FLEXION,
            upperArmFrequencyMap.get(FORTY_FIVE_TO_90_FLEXION)! + 1
          );
        } else {
          upperArmFrequencyMap.set(
            GREATER_THAN_90_FLEXION,
            upperArmFrequencyMap.get(GREATER_THAN_90_FLEXION)! + 1
          );
        }
        return upperArmFrequencyMap;
      },
      upperArmFrequencyMap
    );
  }

  private getLowerArmFrequencyMap(
    lowerArmSagAngles: number[]
  ): Map<string, number> {
    let lowerArmFrequencyMap = new Map<string, number>([
      [LESS_THAN_60_FLEXION, 0],
      [SIXTY_TO_100_FLEXION, 0],
      [GREATER_THAN_100_FLEXION, 0],
    ]);

    return lowerArmSagAngles.reduce(
      (lowerArmFrequencyMap: Map<string, number>, lowerArmSagAngle: number) => {
        if (lowerArmSagAngle < 60) {
          lowerArmFrequencyMap.set(
            LESS_THAN_60_FLEXION,
            lowerArmFrequencyMap.get(LESS_THAN_60_FLEXION)! + 1
          );
        } else if (lowerArmSagAngle >= 60 && lowerArmSagAngle <= 100) {
          lowerArmFrequencyMap.set(
            SIXTY_TO_100_FLEXION,
            lowerArmFrequencyMap.get(SIXTY_TO_100_FLEXION)! + 1
          );
        } else {
          lowerArmFrequencyMap.set(
            GREATER_THAN_100_FLEXION,
            lowerArmFrequencyMap.get(GREATER_THAN_100_FLEXION)! + 1
          );
        }
        return lowerArmFrequencyMap;
      },
      lowerArmFrequencyMap
    );
  }
  private getLegFrequencyMap(kneeSagAngles: number[]): Map<string, number> {
    let kneeFrequencyMap = new Map<string, number>([
      [LESS_THAN_30_FLEXION, 0],
      [THIRTY_TO_60_FLEXION, 0],
      [SIXTY_TO_90_FLEXION, 0],
      [GREATER_THAN_90_FLEXION, 0],
    ]);

    return kneeSagAngles.reduce(
      (kneeFrequencyMap: Map<string, number>, kneeSagAngle: number) => {
        if (kneeSagAngle < 30) {
          kneeFrequencyMap.set(
            LESS_THAN_30_FLEXION,
            kneeFrequencyMap.get(LESS_THAN_30_FLEXION)! + 1
          );
        } else if (kneeSagAngle >= 30 && kneeSagAngle <= 60) {
          kneeFrequencyMap.set(
            THIRTY_TO_60_FLEXION,
            kneeFrequencyMap.get(THIRTY_TO_60_FLEXION)! + 1
          );
        } else if (kneeSagAngle >= 60 && kneeSagAngle <= 90) {
          kneeFrequencyMap.set(
            SIXTY_TO_90_FLEXION,
            kneeFrequencyMap.get(SIXTY_TO_90_FLEXION)! + 1
          );
        } else {
          kneeFrequencyMap.set(
            GREATER_THAN_90_FLEXION,
            kneeFrequencyMap.get(GREATER_THAN_90_FLEXION)! + 1
          );
        }
        return kneeFrequencyMap;
      },
      kneeFrequencyMap
    );
  }
}

export default new PercentagePerBodyPartChartDataService();
